import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Trophy, Upload, FileText, CheckCircle2, AlertCircle, Trash2, Download, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

function prettyBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function formatDeadline(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) +
    " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function CountdownTimer({ deadlineIso }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!deadlineIso) return null;
  const target = new Date(deadlineIso).getTime();
  const remaining = target - now;
  const past = remaining <= 0;
  const totalSec = Math.max(0, Math.floor(remaining / 1000));
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const tone = past ? "bg-[#A23232] text-white"
    : remaining < 1000 * 60 * 60 * 24 ? "bg-[#A23232] text-white"
    : remaining < 1000 * 60 * 60 * 48 ? "bg-[#f4b847] text-[#0F1F2E]"
    : "bg-[#0F1F2E] text-[#F4B847]";
  return (
    <div className={`border-2 border-[#0F1F2E] p-5 md:p-6 ${tone}`} data-testid="final-pitch-countdown">
      <div className="text-[10px] font-semibold tracking-[0.16em] uppercase opacity-90">
        Deadline · {formatDeadline(deadlineIso)}
      </div>
      {past ? (
        <div className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-2">Deadline passed — contact your admin.</div>
      ) : (
        <div className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2 flex flex-wrap gap-x-6 gap-y-2">
          <span><span className="tabular-nums">{d}</span><span className="text-xs uppercase font-semibold tracking-wider ml-1 opacity-80">days</span></span>
          <span><span className="tabular-nums">{String(h).padStart(2, "0")}</span><span className="text-xs uppercase font-semibold tracking-wider ml-1 opacity-80">hr</span></span>
          <span><span className="tabular-nums">{String(m).padStart(2, "0")}</span><span className="text-xs uppercase font-semibold tracking-wider ml-1 opacity-80">min</span></span>
        </div>
      )}
    </div>
  );
}

export default function FinalPitch() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [pitch, setPitch] = useState(null);
  const [busyDeck, setBusyDeck] = useState(false);
  const [programme, setProgramme] = useState({});
  const [exitStatus, setExitStatus] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: p }, { data: prog }, { data: xs }] = await Promise.all([
        api.get("/final-pitch/my"),
        api.get("/programme").catch(() => ({ data: {} })),
        api.get("/team-exit-status").catch(() => ({ data: null })),
      ]);
      setPitch(p);
      setProgramme(prog || {});
      setExitStatus(xs);
    } catch (e) {
      setPitch({ error: e?.response?.data?.detail || "Could not load your team's pitch" });
    }
  };

  useEffect(() => { load(); }, []);

  const pitchMinutes = Number(programme?.pitch_minutes);
  const pitchQAMinutes = Number(programme?.pitch_qa_minutes);
  const hasTiming = pitchMinutes > 0;

  const uploadDeck = async (file) => {
    setBusyDeck(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/final-pitch/deck", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPitch(data);
      toast.success("Pitch deck uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusyDeck(false); }
  };

  const removeDeck = async () => {
    if (!(await confirm({ title: "Remove your pitch deck?", description: "You'll need to re-upload before the deadline.", destructive: true }))) return;
    try {
      const { data } = await api.delete("/final-pitch/deck");
      setPitch(data);
      toast.success("Deck removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  if (!user?.team_id) {
    return (
      <div className="space-y-6" data-testid="final-pitch-no-team">
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#25AAAA] flex items-center gap-2">
          <Trophy size={12} /> Final pitch
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-[#0F1F2E]">Join your team first</h1>
        <p className="text-[#525252] max-w-2xl text-sm leading-relaxed">You need to be on a team to upload the final deck.</p>
        <Link to="/team-setup"><Button className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]">Enter join code</Button></Link>
      </div>
    );
  }

  const deckCurrent = pitch && pitch.deck_file_id ? {
    file_id: pitch.deck_file_id, filename: pitch.deck_filename, size: pitch.deck_size,
    uploaded_at: pitch.deck_uploaded_at, uploaded_by_name: pitch.deck_uploaded_by_name,
  } : null;

  const gateClosed = false;  // Deck upload no longer gated on exit surveys — the only survey-gated benefit is now the LinkedIn recommendation opt-in.

  return (
    <div className="space-y-8" data-testid="final-pitch-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#25AAAA] flex items-center gap-2">
          <Trophy size={12} /> Final pitch
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2 text-[#0F1F2E]">
          Upload your team's final pitch deck.
        </h1>
      </div>

      <CountdownTimer deadlineIso={pitch?.deadline_iso || "2026-12-08T23:59:00+00:00"} />

      {/* Exit-survey tripwire */}
      {gateClosed ? (
        <div className="border-2 border-[#A23232] bg-[#FDEDED] p-5 md:p-6" data-testid="final-pitch-tripwire">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#A23232] text-white flex items-center justify-center shrink-0">
              <Lock size={22} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#A23232]">Deck upload locked</div>
              <h3 className="font-display text-2xl font-black tracking-tight text-[#7A1F1F] mt-1">
                Everyone on your team needs to complete the exit survey first.
              </h3>
              <p className="text-sm text-[#7A1F1F] mt-2 leading-relaxed">
                {exitStatus.completed} of {exitStatus.total} done. Waiting on:
              </p>
              <ul className="mt-2 flex flex-wrap gap-2" data-testid="tripwire-pending-list">
                {exitStatus.pending.map((m) => (
                  <li key={m.id} className="text-xs bg-white border border-[#A23232] px-3 py-1 font-semibold text-[#7A1F1F]">
                    <Users size={11} className="inline mr-1"/>{m.name}
                  </li>
                ))}
              </ul>
              <Link to="/survey/exit" className="mt-4 inline-block text-sm font-semibold uppercase tracking-wide text-[#A23232] hover:underline">
                → Open the exit survey
              </Link>
            </div>
          </div>
        </div>
      ) : deckCurrent ? (
        <div className="border-2 border-[#0A8C5C] bg-[#E8F7EE] p-5" data-testid="final-pitch-all-done">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={28} className="text-[#0A8C5C] shrink-0" />
            <div>
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#0A8C5C]">Submission complete</div>
              <div className="font-display text-xl font-black tracking-tight mt-0.5 text-[#0F1F2E]">
                Deck submitted. You can replace it up to the deadline.
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Upload zone */}
      <div className={`border-2 p-5 md:p-6 ${deckCurrent ? "border-[#0A8C5C] bg-[#E8F7EE]" : gateClosed ? "border-[#E5E7EB] bg-[#F3F4F6] opacity-70" : "border-dashed border-[#0F1F2E] bg-white"}`} data-testid="final-pitch-deck-zone">
        <div className="flex items-start gap-4">
          <div className={`shrink-0 w-12 h-12 flex items-center justify-center ${deckCurrent ? "bg-[#0A8C5C] text-white" : "bg-[#0F1F2E] text-[#F4B847]"}`}>
            {deckCurrent ? <CheckCircle2 size={22} /> : <FileText size={22} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Pitch deck</div>
            <h3 className="font-display text-xl md:text-2xl font-black tracking-tight mt-1 text-[#0F1F2E]">
              {deckCurrent ? "Submitted" : "Upload your pitch deck"}
            </h3>
            {deckCurrent ? (
              <div className="mt-2 space-y-1 text-sm text-[#0F1F2E]">
                <div className="font-medium break-all" data-testid="deck-filename">{deckCurrent.filename}</div>
                <div className="text-xs text-[#525252]">
                  {prettyBytes(deckCurrent.size)} · uploaded by <strong>{deckCurrent.uploaded_by_name || "—"}</strong>
                  {deckCurrent.uploaded_at && <> · {new Date(deckCurrent.uploaded_at).toLocaleString()}</>}
                </div>
                <div className="flex flex-wrap gap-2 pt-3">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${deckCurrent.file_id}/download?auth=${encodeURIComponent(localStorage.getItem("weave_token") || "")}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-[#0F1F2E] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0F1F2E] hover:text-white"
                    data-testid="deck-download">
                    <Download size={14} /> Download
                  </a>
                  <Button onClick={() => fileRef.current?.click()} disabled={busyDeck || gateClosed} variant="outline" className="rounded-none" data-testid="deck-replace">
                    <Upload size={14} className="mr-2" /> Replace
                  </Button>
                  <Button onClick={removeDeck} disabled={busyDeck} variant="outline" className="rounded-none border-[#A23232] text-[#A23232] hover:bg-[#FDEDED]" data-testid="deck-remove">
                    <Trash2 size={14} className="mr-2" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#525252] mt-2 leading-relaxed">
                  Accepted: .pptx, .ppt, .pdf, .key. One file per team — replacing is fine.
                </p>
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={busyDeck || gateClosed}
                  className="mt-4 rounded-none bg-[#0F1F2E] text-[#F4B847] hover:bg-[#25AAAA] hover:text-white h-12 px-6"
                  data-testid="deck-pick">
                  <Upload size={16} className="mr-2" /> {busyDeck ? "Uploading…" : "Choose deck file"}
                </Button>
              </>
            )}
            <input ref={fileRef} type="file" accept=".pptx,.ppt,.pdf,.key" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadDeck(f); e.target.value = ""; } }}
              data-testid="deck-input"/>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-[#25AAAA] bg-[#E6F7F7] px-4 py-3 text-sm text-[#1e7d7d] flex items-start gap-2">
        <AlertCircle size={16} className="shrink-0 mt-0.5" />
        <span>Uploads are shared across your team — any member can upload, replace or remove. The latest overwrites.</span>
      </div>

      {/* Presentation guidance */}
      <article className="space-y-6 max-w-none">
        <div className="border-t border-[#E5E7EB] pt-6">
          <h2 className="font-display text-2xl font-black tracking-tighter text-[#0F1F2E]">Presentation format</h2>
          <div className="mt-3 text-sm md:text-base text-[#0F1F2E] leading-relaxed space-y-3">
            {hasTiming ? (
              <ul className="pl-5 list-disc space-y-1.5">
                <li><strong>{pitchMinutes} minutes to present</strong></li>
                {pitchQAMinutes > 0 && <li><strong>{pitchQAMinutes} minutes of questions from the sponsor</strong></li>}
              </ul>
            ) : (
              <p className="text-[#525252]">Your team will present to the sponsor business. Exact timing will be confirmed by admin closer to the day.</p>
            )}
            <p>The sponsor will listen, ask questions and give you feedback. Bring your best thinking — this is real work for a real business.</p>
          </div>
        </div>

        <div className="border-t border-[#E5E7EB] pt-6">
          <h2 className="font-display text-2xl font-black tracking-tighter text-[#0F1F2E]">A good deck</h2>
          <ul className="mt-3 pl-5 list-disc space-y-1.5 text-sm text-[#0F1F2E]">
            <li>Clear problem statement</li>
            <li>Evidence from your research</li>
            <li>A solution the sponsor can actually use</li>
            <li>How you'd measure it working</li>
            <li>What you'd do next if they said yes</li>
          </ul>
        </div>
      </article>
    </div>
  );
}
